import type { FunctionComponent, SVGProps } from "react";

import JuisciAppIcon from "../../assets/core/icon-square.svg?react";
import JuisciTaglineEn from "../../assets/core/juisci-english-tagline.svg?react";
import JuisciTaglineEnMono from "../../assets/core/juisci-english-tagline-mono.svg?react";
import JuisciTaglineFr from "../../assets/core/juisci-french-tagline.svg?react";
import JuisciTaglineFrMono from "../../assets/core/juisci-french-tagline-mono.svg?react";
import JuisciNoTagline from "../../assets/core/juisci-no-tagline.svg?react";
import JuisciNoTaglineMono from "../../assets/core/juisci-no-tagline-mono.svg?react";
import JuisciOriginals from "../../assets/core/juisci-original.svg?react";
import JuisciOriginalsMono from "../../assets/core/juisci-original-mono.svg?react";
import JuisciSymbol from "../../assets/core/pipe-icon.svg?react";
import JuisciSymbolMono from "../../assets/core/pipe-icon-mono.svg?react";
import { cn } from "../../lib/utils";

/**
 * The lockups that carry the brand in the product, in the order the Design
 * System presents them. This array is the single source of truth: the variant
 * union derives from it and `TONAL_LOCKUPS` must cover it exhaustively
 * (`satisfies Record<…>`), so adding an asset without listing it here fails
 * the build. The square app icon is deliberately absent — see `LogoProps`.
 */
const TONAL_LOCKUP_NAMES = [
  "no-tagline",
  "tagline-en",
  "tagline-fr",
  "originals",
  "symbol",
] as const;

type TonalLockup = (typeof TONAL_LOCKUP_NAMES)[number];

/**
 * The three cuts every lockup ships in:
 *  - `color`: the artwork's own palette — ink letters, juice-filled tube, and
 *    the pink accents in the baselines. The default everywhere it can breathe.
 *  - `black`: one ink, for print, watermarks and partner documents that forbid
 *    a second colour.
 *  - `white`: one ink, for dark surfaces and photography.
 */
const LOGO_TONE_NAMES = ["color", "black", "white"] as const;

type LogoTone = (typeof LOGO_TONE_NAMES)[number];

/**
 * A monochrome cut is NOT the colour artwork with its fills swapped: the juice
 * inside the tube is a filled shape, so recolouring it turns the tube into a
 * solid blob instead of the outline the brand draws. The `*-mono.svg` assets
 * drop the juice path and put every remaining stroke on `currentColor`, so the
 * tone here only has to set the ink.
 */
const toneClasses = {
  color: "",
  black: "text-ds-black",
  white: "text-ds-white",
} as const satisfies Record<LogoTone, string>;

type SvgComponent = FunctionComponent<SVGProps<SVGSVGElement>>;

type LockupSpec = {
  /** Full-palette artwork, used by `tone="color"`. */
  ColorSvg: SvgComponent;
  /** Juice-less artwork on `currentColor`, used by `black` and `white`. */
  MonoSvg: SvgComponent;
  /** Accessible name — a brand mark is content, not decoration. */
  label: string;
  /**
   * Default height class, picked so every lockup reads at a comparable optical
   * weight. Width always follows the intrinsic ratio (`w-auto`), and `cn`
   * (tailwind-merge) lets a call site override it with its own `h-*`.
   */
  heightClass: string;
};

const TONAL_LOCKUPS = {
  /**
   * The primary "JUISCI" lockup: the tagline lockup's mark on its own, with the
   * baseline glyphs cropped away.
   *
   * It replaced a second, narrower `wordmark` drawing (`juisci-logo.svg`) that
   * this component used to expose alongside it. The two were never
   * interchangeable — different artwork, different proportions — and keeping
   * both meant maintaining two monochrome cuts. One of them was wrong: the
   * wordmark's mono was generated by deleting the juice path, but that drawing
   * relies on the juice to OCCLUDE a full-length straw, so the reversed mark
   * showed the straw running to the bottom of the tube. This drawing needs no
   * such trick — its straw is natively short, which is why dropping the juice
   * leaves it correct.
   */
  "no-tagline": {
    ColorSvg: JuisciNoTagline,
    MonoSvg: JuisciNoTaglineMono,
    label: "Juisci",
    heightClass: "h-20",
  },
  /** Vertical lockup with the English baseline. */
  "tagline-en": {
    ColorSvg: JuisciTaglineEn,
    MonoSvg: JuisciTaglineEnMono,
    label: "Juisci — Your juice of science",
    heightClass: "h-28",
  },
  /** Vertical lockup with the French baseline. */
  "tagline-fr": {
    ColorSvg: JuisciTaglineFr,
    MonoSvg: JuisciTaglineFrMono,
    label: "Juisci — Ton jus de science",
    heightClass: "h-28",
  },
  /** Sub-brand lockup, stamped on Juisci-produced content. */
  originals: {
    ColorSvg: JuisciOriginals,
    MonoSvg: JuisciOriginalsMono,
    label: "Juisci Originals",
    heightClass: "h-6",
  },
  /** The test tube alone — avatars, watermarks, tight nav slots. */
  symbol: {
    ColorSvg: JuisciSymbol,
    MonoSvg: JuisciSymbolMono,
    label: "Juisci",
    heightClass: "h-10",
  },
} as const satisfies Record<TonalLockup, LockupSpec>;

/**
 * The app icon is a composed artifact — the tube already plated on its light
 * square — so it has no tonal cuts, and modelling it as its own arm keeps
 * `tone` from being requested where it means nothing.
 */
type LogoProps = { className?: string } & (
  { variant: TonalLockup; tone: LogoTone } | { variant: "app-icon" }
);

/**
 * Juisci brand mark (Figma "Juisci Design System 5.0 → Logo").
 *
 * Every lockup renders at its intrinsic aspect ratio: pick the height and the
 * width follows. Pick the `tone` from the surface — `color` on light, `white`
 * on dark or photographic, `black` when a second ink is not available. Never
 * recolour a lockup by hand.
 */
function Logo(props: LogoProps): React.JSX.Element {
  if (props.variant === "app-icon")
    return (
      <JuisciAppIcon
        role="img"
        aria-label="Juisci"
        className={cn("h-16 w-auto", props.className)}
      />
    );

  const { ColorSvg, MonoSvg, label, heightClass } =
    TONAL_LOCKUPS[props.variant];
  const Svg = props.tone === "color" ? ColorSvg : MonoSvg;
  return (
    <Svg
      role="img"
      aria-label={label}
      className={cn(
        heightClass,
        "w-auto",
        toneClasses[props.tone],
        props.className
      )}
    />
  );
}

export {
  Logo,
  LOGO_TONE_NAMES,
  TONAL_LOCKUP_NAMES,
  type LogoProps,
  type LogoTone,
  type TonalLockup,
};
